import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import SEOHead from '../components/common/SEOHead';
import { usePageSeo } from '../hooks/useSeoConfig';
import { useLevelsData } from '../services/levelsService';
import LevelDetailModal from '../components/ui/LevelDetailModal';

const TYPE_MAP = {
    aeroplane: 'Avion',
    helicopter: 'Helicoptere',
    glider: 'Planeur',
    dispatch: 'Dispatch',
};

const TYPE_CONFIG = {
    aeroplane: { labelFr: 'Avion', labelEn: 'Airplane' },
    helicopter: { labelFr: 'Hélico', labelEn: 'Helicopter' },
    glider: { labelFr: 'Planeur', labelEn: 'Glider' },
    dispatch: { labelFr: 'Dispatch', labelEn: 'Dispatch' },
};

const TYPE_ORDER = ['aeroplane', 'helicopter', 'glider', 'dispatch'];

function LevelsAndSubscriptions() {
    const { language } = useLanguage();
    const [searchParams] = useSearchParams();
    const { levelsData, loading } = useLevelsData();

    const urlType = searchParams.get('type') || 'aeroplane';
    const urlLevelId = searchParams.get('level') || searchParams.get('stage');

    const [selectedType, setSelectedType] = useState(urlType);
    const [selectedLevel, setSelectedLevel] = useState(null);

    const allLevels = levelsData?.levels || [];

    const uniqueTypes = Array.from(
        new Set(
            allLevels.flatMap(l => {
                const types = [];
                (l.application || '').split(' ').forEach(t => {
                    const internal = Object.entries(TYPE_MAP).find(([, v]) => v === t);
                    if (internal) types.push(internal[0]);
                });
                return types;
            })
        )
    ).sort((a, b) => TYPE_ORDER.indexOf(a) - TYPE_ORDER.indexOf(b));

    const appType = TYPE_MAP[selectedType];
    const filteredLevels = allLevels.filter(
        l => (l.application || '').includes(appType) && l.idNiveau !== 0
    );

    useEffect(() => {
        if (urlLevelId && allLevels.length > 0) {
            const numId = parseInt(urlLevelId);
            const found = allLevels.find(l => l.idNiveau === numId || l.var === urlLevelId);
            if (found) setSelectedLevel(found);
        }
    }, [urlLevelId, allLevels]);

    const seo = usePageSeo('page_levels');
    const fr = language === 'fr';

    return (
        <>
            <SEOHead
                title={fr ? (seo.title_fr || 'Licences & qualifications') : (seo.title_en || 'Licences & Ratings')}
                description={fr ? seo.desc_fr : seo.desc_en}
                keywords={fr ? seo.keywords_fr : seo.keywords_en}
                canonical="/levels"
            />

            {/* Hero */}
            <div className="max-w-5xl mx-auto px-6 pt-12 pb-10 text-center">
                <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-[1.05] mb-4">
                    <span className="text-gray-900 dark:text-white">{fr ? 'Licences' : 'Licences'}</span><br />
                    <span className="inline-block px-3 pb-3 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300 italic leading-[1.15]">{fr ? '& qualifications' : '& Ratings'}</span>
                </h1>
                <p className="text-base text-gray-500 dark:text-white/50 max-w-md mx-auto">
                    {fr
                        ? 'Choisissez votre formation et sa durée'
                        : 'Choose your training and its duration'}
                </p>
            </div>

            {/* Type selector — pill style */}
            <div className="max-w-md mx-auto px-6 mb-12">
                <div className="flex bg-gray-100 dark:bg-white/5 rounded-full p-1 border border-gray-200 dark:border-white/[0.06]">
                    {uniqueTypes.map(typeId => {
                        const config = TYPE_CONFIG[typeId];
                        const isActive = selectedType === typeId;
                        const label = fr ? config.labelFr : config.labelEn;

                        return (
                            <button
                                key={typeId}
                                onClick={() => setSelectedType(typeId)}
                                className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all ${
                                    isActive
                                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                                        : 'text-gray-500 dark:text-white/40 hover:text-gray-700 dark:hover:text-white/70'
                                }`}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Level cards grid */}
            <section className="max-w-5xl mx-auto px-6 pb-24">
                {loading ? (
                    <div className="text-center py-16">
                        <div className="w-8 h-8 border-2 border-gray-300 dark:border-white/20 border-t-gray-700 dark:border-t-white rounded-full animate-spin mx-auto" />
                    </div>
                ) : filteredLevels.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {filteredLevels.map(level => {
                            const name = fr ? level.libelleFr : level.libelleEn;
                            const description = fr ? level.newPageFr : level.newPageEn;
                            const p1 = level.pricing?.find(p => p.duration === 1);
                            const priceExam = p1?.priceExam != null ? parseFloat(p1.priceExam) : null;

                            return (
                                <button
                                    key={level.idNiveau}
                                    onClick={() => setSelectedLevel(level)}
                                    className="group bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] rounded-2xl p-6 text-left transition-all hover:bg-gray-50 dark:hover:bg-white/[0.06] hover:border-gray-300 dark:hover:border-white/[0.12] hover:scale-[1.02] flex flex-col"
                                >
                                    {/* Badge */}
                                    <div className="flex items-center gap-3 mb-4">
                                        <div
                                            className="px-3 py-1.5 rounded-xl text-white font-black text-xs whitespace-nowrap"
                                            style={{ backgroundColor: level.newBackgroundColor }}
                                        >
                                            {(name || level.var).toUpperCase()}
                                        </div>
                                        {level.isFree && (
                                            <span className="text-xs font-bold text-green-400 bg-green-500/10 px-2.5 py-1 rounded-full">
                                                {fr ? 'Gratuit' : 'Free'}
                                            </span>
                                        )}
                                    </div>

                                    {/* Description */}
                                    {description && (
                                        <p className="text-sm text-gray-500 dark:text-white/40 line-clamp-3 mb-5 flex-1 leading-relaxed">
                                            {description}
                                        </p>
                                    )}

                                    {/* Price */}
                                    <div className="mt-auto pt-4 border-t border-gray-200 dark:border-white/[0.06]">
                                        {level.isFree ? (
                                            <p className="text-sm font-bold text-green-400">
                                                {fr ? 'Accès libre' : 'Free access'}
                                            </p>
                                        ) : priceExam != null ? (
                                            <p className="flex items-baseline gap-2">
                                                <span className="text-[10px] font-semibold text-gray-400 dark:text-white/30 uppercase tracking-wider">
                                                    {fr ? 'À partir de' : 'Starting at'}
                                                </span>
                                                <span className="font-black text-gray-900 dark:text-white text-2xl">{priceExam}$</span>
                                            </p>
                                        ) : null}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-16">
                        <p className="text-gray-500 dark:text-white/40">
                            {fr
                                ? 'Aucun niveau disponible pour cette catégorie.'
                                : 'No levels available for this category.'}
                        </p>
                    </div>
                )}
            </section>

            {/* Detail modal */}
            <AnimatePresence>
                {selectedLevel && (
                    <LevelDetailModal
                        key={selectedLevel.idNiveau}
                        level={selectedLevel}
                        allLevels={allLevels}
                        onClose={() => setSelectedLevel(null)}
                    />
                )}
            </AnimatePresence>
        </>
    );
}

export default LevelsAndSubscriptions;
